<div class="tile-padding-first tile-padding">

  <div class="qa-filter-bar">

    <div class="qa-filter-bar__field">
      <label class="field__label">Brand</label>
      <input type="text" class="field__control" [value]="brandLabel" disabled readonly
        title="QA approval is currently FES only">
    </div>

    <div class="qa-filter-bar__field">
      <label class="field__label">Tuition consultant</label>
      <app-searchable-select [(ngModel)]="selectedConsultantIds" [items]="consultants"
        displayField="name" valueField="id" multiple
        placeholder="Select at least one consultant"
        searchPlaceholder="Search consultants…"></app-searchable-select>
    </div>

    <div class="qa-filter-bar__field">
      <label class="field__label">Report type</label>
      <!-- [value], not [ngValue]: all three are plain strings, so there is no null to preserve. -->
      <select class="field__control" [(ngModel)]="selectedReportType">
        @for (option of reportTypeOptions; track option.value) {
          <option [value]="option.value">{{option.label}}</option>
        }
      </select>
    </div>

    <!-- No (change) on the filters: the grid deliberately keeps the last result set up until
         Search is pressed, so a half-changed filter never silently empties the screen. -->
    <div class="qa-filter-bar__actions">
      <button class="btn btn-outline-primary" type="button" (click)="reset()">Reset</button>
      <button class="btn btn-primary" type="button" (click)="run()"
        [disabled]="!canRun || loading">Search</button>
    </div>

  </div>

  @if (noConsultantsAvailable) {
    <p class="qa-no-consultants">There are no FES reports awaiting approval, so there are no consultants to
      filter by. Reports become available here the day after they are submitted.</p>
  }

  @if (searchRun) {
    <div class="qa-results-header">
      <strong>{{checkedCount}} {{checkedCount === 1 ? 'report' : 'reports'}} will be updated</strong>
    </div>

    @if (rows.length > 0) {
      <label class="field__checkbox">
        <input type="checkbox" [(ngModel)]="selectAll"
          (change)="onAllSelected({checked: $event.target.checked})" /> Select All
      </label>
    }

    <!-- One card holds whichever review table is on screen plus the shared footer, the same shape the
         shared data table uses (.dt-container wrapping .dt-scroll and its pagination). -->
    <div class="qa-review-container" [class.qa-review-container--filled]="rows.length > 0">

    <!-- Session reports get the review table: session-level cells span each session's learner rows, so
         a learner's name, attendance and two comment columns always line up on the same band. -->
    @if (sessionRows.length > 0) {
      <div class="qa-session-scroll">
        <table class="qa-session-table">
          <thead>
            <tr>
              <th class="qa-col-select"></th>
              <th>Report</th>
              <th>Learning Objectives</th>
              <th>Learner</th>
              <th>Attendance</th>
              <th>Comments on Progress</th>
              <th>Engagement Comments</th>
              <th class="qa-col-actions"></th>
            </tr>
          </thead>

          @for (row of sessionRows; track row.reportType + ':' + row.id) {
            <tbody class="qa-session" [class.qa-session--selected]="checked(row)">
              @for (learner of learnersFor(row); track learner.studentId; let first = $first) {
                <tr>
                  @if (first) {
                    <td class="qa-col-select" [attr.rowspan]="learnerCount(row)">
                      <label class="field__checkbox">
                        <input type="checkbox" [checked]="checked(row)"
                          (change)="assignCheckbox(row, {checked: $event.target.checked})" />
                      </label>
                    </td>

                    <td [attr.rowspan]="learnerCount(row)">
                      <div class="qa-report-tutor">{{row.session.tutorName}}</div>
                      <div class="qa-report-meta">{{row.session.sessionType}}</div>
                      @if (deliverySettingOn && (row.session.deliverySetting || row.session.deliveryMode)) {
                        <div class="qa-report-meta" data-test="qa-session-delivery">{{row.session.deliverySetting || row.session.deliveryMode}}</div>
                      }
                      <div class="qa-report-meta">{{row.session.subject}}</div>
                      <div class="qa-report-meta">{{row.consultantName}}</div>
                    </td>

                    <td [attr.rowspan]="learnerCount(row)">
                      @for (objective of row.session.objectives; track $index; let i = $index) {
                        <div class="qa-objective">
                          <div class="qa-objective__label">Objective {{i + 1}}</div>
                          <div>{{objective}}</div>
                        </div>
                      }
                    </td>
                  }

                  <td>
                    <div class="qa-learner-name">{{learner.name}}</div>
                    <div class="qa-learner-level">{{learner.keyStage}}</div>
                  </td>

                  <td>
                    @if (learner.attendanceStatus) {
                      <app-status-pill [label]="learner.attendanceStatus"
                        [variant]="attendanceVariant(learner)"></app-status-pill>
                      <div class="qa-attendance-reason"><strong>Reason:</strong> {{learner.attendanceReason || '—'}}</div>
                      <div>{{learner.attendanceComments}}</div>
                    }
                  </td>

                  <td>
                    @if (learnerAttended(learner)) {
                      {{learner.objectiveComments}}
                    } @else {
                      <span class="qa-not-completed">Not completed</span>
                    }
                  </td>

                  <td>
                    @if (learnerAttended(learner)) {
                      {{learner.engagementComments}}
                    } @else {
                      <span class="qa-not-completed">Not completed</span>
                    }
                  </td>

                  @if (first) {
                    <td class="qa-col-actions" [attr.rowspan]="learnerCount(row)">
                      @if (canApprove) {
                        <button class="btn btn-primary" type="button" (click)="approveSession(row)">Approve</button>
                      }
                      @if (canReject) {
                        <button class="btn btn-outline-primary" type="button" (click)="rejectSession(row)">Reject</button>
                      }
                      <button class="btn btn-outline-primary" type="button" (click)="openReport(row)">Open report</button>
                    </td>
                  }
                </tr>
              }
            </tbody>
          }
        </table>
      </div>
    }

    <!-- ILPs need no rowspan: tblBaseline's key is (placement, student), so one ILP is one learner. -->
    @if (ilpRows.length > 0) {
      <div class="qa-session-scroll">
        <table class="qa-session-table">
          <thead>
            <tr>
              <th class="qa-col-select"></th>
              <th>Report</th>
              <th>Learner</th>
              <th>Assessment</th>
              <th>Areas of strength</th>
              <th>Areas for development</th>
              <th>Plan to achieve progress</th>
              <th>Learner attributes</th>
              <th class="qa-col-actions">Actions</th>
            </tr>
          </thead>

          <tbody>
            @for (row of ilpRows; track row.reportType + ':' + row.id) {
              <tr class="qa-review-row" [class.qa-review-row--selected]="checked(row)">
                <td class="qa-col-select">
                  <label class="field__checkbox">
                    <input type="checkbox" [checked]="checked(row)"
                      (change)="assignCheckbox(row, {checked: $event.target.checked})" />
                  </label>
                </td>

                <td>
                  <div class="qa-report-tutor">{{row.ilp.tutorName}}</div>
                  <div class="qa-report-meta">{{row.ilp.provisionType}}</div>
                  <div class="qa-report-meta">{{row.ilp.subject}}</div>
                  <div class="qa-report-meta">{{row.consultantName}}</div>
                </td>

                <td>
                  <div class="qa-learner-name">{{row.ilp.studentName}}</div>
                  <div class="qa-learner-level">{{row.ilp.keyStage}}</div>
                </td>

                <td>
                  <div class="qa-assessment">
                    <div class="qa-assessment__score">
                      <div class="qa-assessment__value">{{row.ilp.initialScore}}%</div>
                      <div class="qa-assessment__caption">Initial</div>
                    </div>
                    <div class="qa-assessment__arrow">&rarr;</div>
                    <div class="qa-assessment__score">
                      <div class="qa-assessment__value">{{row.ilp.targetScore}}%</div>
                      <div class="qa-assessment__caption">Target</div>
                    </div>
                    @if (scoreDelta(row) !== null) {
                      <div class="qa-assessment__delta">{{scoreDelta(row) > 0 ? '+' : ''}}{{scoreDelta(row)}}</div>
                    }
                  </div>
                  <div class="qa-assessment__used">{{row.ilp.assessmentUsed}}</div>
                </td>

                <td>{{row.ilp.areasOfStrength}}</td>
                <td>{{row.ilp.areasForDevelopment}}</td>
                <td>{{row.ilp.plan}}</td>

                <td>
                  @for (attribute of ilpAttributes; track attribute.key) {
                    <div class="qa-attribute">
                      <span class="qa-attribute__label">{{attribute.label}}</span>
                      <span class="qa-attribute__value"
                        [style.color]="attributeColour(attributeValue(row, attribute.key))">
                        {{attributeLabel(attributeValue(row, attribute.key))}}
                      </span>
                    </div>
                  }
                </td>

                <td class="qa-col-actions">
                  @if (canApproveIlp) {
                    <button class="btn btn-primary" type="button" (click)="approveIlp(row)">Approve</button>
                  }
                  @if (canRejectIlp) {
                    <button class="btn btn-outline-primary" type="button" (click)="rejectIlp(row)">Reject</button>
                  }
                  <button class="btn btn-outline-primary" type="button" (click)="openIlpReport(row)">Open report</button>
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    }

    <!-- Progress reports: one <tr> each. A learner has many, but each is still one learner. -->
    @if (progressRows.length > 0) {
      <div class="qa-session-scroll">
        <table class="qa-session-table">
          <thead>
            <tr>
              <th class="qa-col-select"></th>
              <th>Report</th>
              <th>Learner</th>
              <th>Academic progress</th>
              <th>SEMH update</th>
              <th>Summary of last 6 weeks</th>
              <th>Plan for next 6 weeks</th>
              <th class="qa-col-actions">Actions</th>
            </tr>
          </thead>

          <tbody>
            @for (row of progressRows; track row.reportType + ':' + row.id) {
              <tr class="qa-review-row" [class.qa-review-row--selected]="checked(row)">
                <td class="qa-col-select">
                  <label class="field__checkbox">
                    <input type="checkbox" [checked]="checked(row)"
                      (change)="assignCheckbox(row, {checked: $event.target.checked})" />
                  </label>
                </td>

                <td>
                  <div class="qa-report-tutor">{{row.progress.tutorName}}</div>
                  <div class="qa-report-meta">{{row.progress.provisionType}}</div>
                  <div class="qa-report-meta">{{row.progress.subject}}</div>
                  <div class="qa-report-meta">{{row.consultantName}}</div>
                </td>

                <td>
                  <div class="qa-learner-name">{{row.progress.studentName}}</div>
                  <div class="qa-learner-level">{{row.progress.keyStage}}</div>
                </td>

                <td>
                  <leap-v2-delta [previous]="row.progress.previousScore" [current]="row.progress.currentScore"
                    display="percent"></leap-v2-delta>
                  <div class="qa-assessment__used">
                    {{progressBaselineCaption(row)}}, {{row.progress.previousDate | date: 'dd MMM yyyy'}}
                    @if (row.progress.targetScore !== null) {
                      · target {{row.progress.targetScore}}%
                    }
                  </div>
                </td>

                <td>
                  @for (attribute of progressAttributes(row); track attribute.label) {
                    <div class="qa-attribute">
                      <span class="qa-attribute__label">{{attribute.label}}</span>
                      <leap-v2-delta [previous]="attribute.previous" [current]="attribute.current"></leap-v2-delta>
                    </div>
                  }
                </td>

                <td>{{row.progress.last6WeeksSummary}}</td>
                <td>{{row.progress.next6WeeksPlan}}</td>

                <td class="qa-col-actions">
                  @if (canApproveProgress) {
                    <button class="btn btn-primary" type="button" (click)="approveProgress(row)">Approve</button>
                  }
                  @if (canRejectProgress) {
                    <button class="btn btn-outline-primary" type="button" (click)="rejectProgress(row)">Reject</button>
                  }
                  <button class="btn btn-outline-primary" type="button" (click)="openProgressReport(row)">Open report</button>
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    }

    <!-- One footer for all three tables: the filter is single-select, so only one is ever on screen. -->
    @if (rows.length > 0) {
      <app-table-pagination
        [page]="page"
        [pageSize]="pageSize"
        [total]="total"
        [pageSizeOptions]="pageSizeOptions"
        (pageChange)="onPageChange($event)"
        (pageSizeChange)="onPageSizeChange($event)">
      </app-table-pagination>
    }

    </div>

    <!-- Every type now has its own table, so this only renders the empty/loading message. -->
    @if (rows.length === 0 || flatRows.length > 0) {
      <app-data-table
        [columns]="columns"
        [data]="flatRows"
        [pageSize]="10"
        [loading]="loading"
        [emptyMessage]="emptyMessage">
        <ng-template appColumnCell="select" let-element>
          <label class="field__checkbox">
            <input type="checkbox" [checked]="checked(element)"
              (change)="assignCheckbox(element, {checked: $event.target.checked})" />
          </label>
        </ng-template>
      </app-data-table>
    }

    @if (rows.length > 0) {
      <div class="btn-align">
        <button class="btn btn-primary tw:mr-1" type="button" (click)="approveAll()"
          [disabled]="!canApproveAll">Approve all</button>
      </div>
    }
  }

</div>
